import { Link } from "react-router-dom";
import { MessageCircle, Phone, ArrowRight, Check } from "lucide-react";
import { Seo } from "../components/Seo";
import { Reveal } from "../components/Reveal";
import { Breadcrumbs } from "../components/Breadcrumbs";
import { LeadForm } from "../components/LeadForm";
import { SITE, img, openEnquiry } from "../config";
import { trackEvent } from "../analytics";
import { CONFIGURATIONS, AMENITY_LIST, CONNECTIVITY } from "../data/site";

const COPY = {
    "1-bhk": {
        title: "Atlanta Enclave 1 BHK | Shilphata",
        desc: "Atlanta Enclave 1 BHK flats in Shilphata, Thane – 520 sq.ft. carpet homes from ₹49.99 Lacs + taxes. View floor plan, amenities & location. RERA P51700077788.",
        h1: "Atlanta Enclave 1 BHK — Flats in Shilphata",
        body: "The 1 BHK residences at Atlanta Enclave Shilphata are compact yet stylish, offering 520 sq.ft. of intelligently planned carpet area with a private sun deck. Built with advanced MIVAN technology for durability, precision and a seamless finish, these homes are ideal for young professionals, first-time buyers and investors exploring 1 BHK flats in Shilphata.",
    },
    "2-bhk": {
        title: "Atlanta Enclave 2 BHK | Shilphata",
        desc: "Atlanta Enclave 2 BHK flats in Shilphata, Thane – 715 sq.ft. carpet homes from ₹69.99 Lacs + taxes. View floor plan, amenities & location. RERA P51700077788.",
        h1: "Atlanta Enclave 2 BHK — Flats in Shilphata",
        body: "The 2 BHK residences at Atlanta Enclave Shilphata offer 715 sq.ft. of carpet area with sunlit interiors, wide balconies and ample storage — perfect for small and growing families. MIVAN construction ensures a precise, long-lasting finish, making these among the most thoughtfully planned 2 BHK flats in Shilphata.",
    },
    "3-bhk": {
        title: "Atlanta Enclave 3 BHK | Shilphata",
        desc: "Atlanta Enclave 3 BHK flats in Shilphata, Thane – 1080 sq.ft. carpet residences at ₹1.40 Cr all inclusive. Amenities, location & floor plan on request. RERA P51700077788.",
        h1: "Atlanta Enclave 3 BHK — Flats in Shilphata",
        body: "The 3 BHK residences at Atlanta Enclave Shilphata are the largest homes in the community — 1080 sq.ft. of luxurious, expansive carpet area crafted for ultimate comfort. With panoramic views, premium finishes and the privacy of an 8-acre gated enclave, these 3 BHK flats in Shilphata are designed for families that want more of everything.",
    },
};

export default function ConfigPage({ config }) {
    const c = CONFIGURATIONS.find((x) => x.key === config);
    const copy = COPY[config];
    const jsonLd = [
        {
            "@context": "https://schema.org",
            "@type": "WebPage",
            name: copy.title,
            url: `https://atlantaenclave.com/${config}/`,
            description: copy.desc,
            isPartOf: { "@type": "WebSite", name: "Atlanta Enclave", url: "https://atlantaenclave.com/" },
        },
    ];

    return (
        <div data-testid={`config-page-${config}`}>
            <Seo title={copy.title} description={copy.desc} path={`/${config}/`} jsonLd={jsonLd} />
            <Breadcrumbs items={[{ label: "Home", to: "/" }, { label: `Atlanta Enclave ${c.name}` }]} />

            <section className="container-x mt-8 grid items-start gap-10 lg:grid-cols-2">
                <Reveal>
                    <p className="eyebrow">{c.area} · RERA {SITE.rera}</p>
                    <h1 className="font-display mt-3 text-4xl font-medium leading-[1.1] text-[#23231f] sm:text-5xl">{copy.h1}</h1>
                    <p className="mt-5 text-sm leading-relaxed text-[#55534a] sm:text-base">{copy.body}</p>

                    <div className="card-line mt-6 flex flex-wrap items-baseline gap-x-3 gap-y-1 p-5" data-testid={`${config}-price-card`}>
                        <span className="text-xs font-bold uppercase tracking-wider text-[#8a877a]">Starting at</span>
                        <span className="font-display text-3xl font-semibold text-[#6e6234]">{c.price}</span>
                        <span className="text-sm text-[#8a877a]">{c.priceNote}</span>
                    </div>

                    <div className="mt-6 flex flex-wrap gap-3">
                        <button onClick={openEnquiry} className="btn btn-gold" data-testid={`${config}-get-price-button`}>
                            Get Price &amp; Floor Plan
                        </button>
                        <a href={SITE.whatsapp} target="_blank" rel="noopener noreferrer" onClick={() => trackEvent("whatsapp_click", { placement: `${config}_page` })} className="btn btn-wa" data-testid={`${config}-whatsapp-button`}>
                            <MessageCircle size={16} /> WhatsApp
                        </a>
                        <a href={SITE.phoneTel} onClick={() => trackEvent("call_click", { placement: `${config}_page` })} className="btn btn-line" data-testid={`${config}-call-button`}>
                            <Phone size={15} /> Call Now
                        </a>
                    </div>
                </Reveal>

                <Reveal delay={120}>
                    {c.plan ? (
                        <figure>
                            <img src={img(c.plan)} alt={`Atlanta Enclave ${c.name} floor plan`} className="frame-img mx-auto w-full max-w-md" loading="lazy" width="473" height="565" data-testid={`${config}-plan-image`} />
                            <figcaption className="mt-2 text-center text-xs text-[#8a877a]">
                                Atlanta Enclave {c.name} floor plan —{" "}
                                <Link to="/floor-plans/" className="font-semibold text-[#6e6234] underline underline-offset-2" data-testid={`${config}-all-plans-link`}>
                                    view all floor plans
                                </Link>
                            </figcaption>
                        </figure>
                    ) : (
                        <figure>
                            <img src={img("elevation")} alt="Atlanta Enclave Shilphata elevation – 3 BHK residences" className="frame-img w-full" loading="lazy" width="1139" height="688" data-testid={`${config}-plan-image`} />
                            <figcaption className="mt-2 text-center text-xs text-[#8a877a]">
                                3 BHK floor plan available on request — enquire below or WhatsApp us.
                            </figcaption>
                        </figure>
                    )}
                </Reveal>
            </section>

            <section className="container-x mt-16 grid gap-10 lg:grid-cols-2">
                <Reveal>
                    <h2 className="font-display text-2xl font-semibold text-[#23231f]">Amenities You&apos;ll Enjoy</h2>
                    <ul className="mt-5 grid grid-cols-1 gap-2.5 sm:grid-cols-2">
                        {AMENITY_LIST.map((a) => (
                            <li key={a} className="flex items-start gap-2.5 text-sm text-[#23231f]">
                                <Check size={16} className="mt-0.5 shrink-0 text-[#8f7f45]" /> {a}
                            </li>
                        ))}
                    </ul>
                    <Link to="/amenities/" className="mt-5 inline-flex items-center gap-2 text-sm font-bold uppercase tracking-wider text-[#23231f] underline decoration-[#8f7f45] underline-offset-4 hover:text-[#6e6234]" data-testid={`${config}-amenities-link`}>
                        Explore Atlanta Enclave amenities <ArrowRight size={15} />
                    </Link>
                </Reveal>
                <Reveal delay={100}>
                    <h2 className="font-display text-2xl font-semibold text-[#23231f]">Location Advantage</h2>
                    <ul className="mt-5 grid gap-2.5">
                        {CONNECTIVITY.slice(0, 5).map(([place, time]) => (
                            <li key={place} className="flex items-baseline justify-between gap-3 border-b border-[#ded8c6] pb-2 text-sm">
                                <span className="text-[#23231f]">{place}</span>
                                <span className="font-bold text-[#6e6234]">{time}</span>
                            </li>
                        ))}
                    </ul>
                    <Link to="/location/" className="mt-5 inline-flex items-center gap-2 text-sm font-bold uppercase tracking-wider text-[#23231f] underline decoration-[#8f7f45] underline-offset-4 hover:text-[#6e6234]" data-testid={`${config}-location-link`}>
                        See Atlanta Enclave location <ArrowRight size={15} />
                    </Link>
                </Reveal>
            </section>

            <section className="container-x mt-16 grid items-start gap-10 lg:grid-cols-2">
                <Reveal>
                    <h2 className="font-display text-2xl font-semibold text-[#23231f]">Get {c.name} Price &amp; Floor Plan</h2>
                    <p className="mt-3 max-w-md text-sm leading-relaxed text-[#55534a]">
                        Prices are subject to change and flexible payment plans are available. Share your details for the latest
                        cost sheet, or{" "}
                        <Link to="/contact/" className="font-semibold text-[#6e6234] underline underline-offset-2" data-testid={`${config}-contact-link`}>
                            book a site visit
                        </Link>
                        .
                    </p>
                </Reveal>
                <Reveal delay={100}>
                    <LeadForm source={`${config}_page`} idPrefix={config} defaultConfig={c.name} heading={`Enquire About Atlanta Enclave ${c.name}`} />
                </Reveal>
            </section>
        </div>
    );
}
